/**
 * Hook da única agenda de Pronto Atendimento (Schedule FHIR).
 * 1 Schedule + 1 HCS; disponibilidade = Schedule.active.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useLocationContext } from "@/contexts/LocationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetSchedule, PatchSchedule } from "@/services/fhir";
import { useFormatter } from "@/utils/useFormatter";
import { PRONTO_ATENDIMENTO_MODE_LABELS } from "./types";
import type {
  ProntoAtendimentoConfig,
  ProntoAtendimentoConfigInput,
} from "./types";
import {
  ensureProntoAtendimentoLocation,
  listPresencialLocations,
} from "./locationHelpers";
import { modeToAttendanceType } from "./buildProntoAtendimentoSchedule";
import { buildProntoHealthcareServicePayload } from "./buildProntoHealthcareServicePayload";
import {
  extractSchedulesAndLocations,
  isProntoAtendimentoSchedule,
  mapScheduleToConfig,
} from "./mapScheduleToConfig";
import {
  clearProntoHealthcareServiceCache,
  extractHealthcareServicesFromBundle,
  fetchHealthcareServiceById,
  getHealthcareServiceIdFromSchedule,
  hasProntoHealthcareServiceTimeWindowForToday,
  isProntoHealthcareServiceWithinWindow,
  PRONTO_ATENDIMENTO_INTERNAL_TOLERANCE_MINUTES,
} from "./isProntoHealthcareServiceAvailableNow";
import { useProntoAtendimentoNow } from "./useProntoAtendimentoNow";
import { postProntoAtendimentoSchedule } from "./postProntoAtendimentoSchedule";
import { putProntoAtendimentoHealthcareService } from "./putProntoAtendimentoHealthcareService";
import {
  PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL,
  PRONTO_ATENDIMENTO_SERVICE_CATEGORY_TOKEN,
  SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
  SERVICE_COST_EXTENSION_URL,
} from "./serviceCategory";

function findExtensionIndex(schedule: fhir5.Schedule, url: string): number {
  return schedule.extension?.findIndex((e) => e.url === url) ?? -1;
}

function pickSingleSchedule(
  schedules: fhir5.Schedule[],
): fhir5.Schedule | null {
  if (schedules.length === 0) return null;
  const active = schedules.filter((s) => s.active !== false);
  const pool = active.length > 0 ? active : schedules;
  const withDefault = pool.find((s) =>
    s.extension?.some(
      (e) =>
        e.url === PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL &&
        e.valueBoolean === true,
    ),
  );
  return withDefault || pool[0];
}

export function useProntoAtendimentoConfigs() {
  const { user, access } = useSessionContext();
  const { location, refetchLocation } = useLocationContext();

  const [config, setConfig] = useState<ProntoAtendimentoConfig | null>(null);
  const [scheduleRaw, setScheduleRaw] = useState<fhir5.Schedule | null>(null);
  const [healthcareService, setHealthcareService] =
    useState<fhir5.HealthcareService | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const practitionerRef = `Practitioner/${oids.cpf}-${user.username}`;
  const practitionerDisplay = useFormatter.formatNameDisplay(user.name || "");
  const organizationToken = access?.organizationId
    ? `${oids.cnpj}-${access.organizationId}`
    : undefined;

  const presencialLocations = useMemo(
    () => listPresencialLocations(location),
    [location],
  );

  const isAvailable = !!config?.active;
  const now = useProntoAtendimentoNow();

  const healthcareServiceId = useMemo(
    () =>
      config?.healthcareServiceId ||
      (scheduleRaw ? getHealthcareServiceIdFromSchedule(scheduleRaw) : undefined),
    [config?.healthcareServiceId, scheduleRaw],
  );
  const hasHcsLink = Boolean(healthcareServiceId);

  const hasTimeWindow = useMemo(
    () => hasProntoHealthcareServiceTimeWindowForToday(healthcareService),
    [healthcareService],
  );

  const isWithinServiceHours = useMemo(
    () =>
      isProntoHealthcareServiceWithinWindow(
        healthcareService,
        now,
        PRONTO_ATENDIMENTO_INTERNAL_TOLERANCE_MINUTES,
      ),
    [healthcareService, now],
  );

  /**
   * UI: disponível só com active + HCS carregado + jornada de hoje dentro da janela.
   * Sem HCS vinculado (legado): mantém só active.
   */
  const switchChecked = useMemo(() => {
    if (!isAvailable) return false;
    if (!hasHcsLink) return true;
    if (!healthcareService) return false;
    if (!hasTimeWindow) return false;
    return isWithinServiceHours;
  }, [
    isAvailable,
    hasHcsLink,
    healthcareService,
    hasTimeWindow,
    isWithinServiceHours,
  ]);

  /** Fora do horário ou sem jornada hoje — induz reativar via config. */
  const isExpiredOutsideHours = useMemo(() => {
    if (!isAvailable || !hasHcsLink || !healthcareService) return false;
    return !hasTimeWindow || !isWithinServiceHours;
  }, [
    isAvailable,
    hasHcsLink,
    healthcareService,
    hasTimeWindow,
    isWithinServiceHours,
  ]);

  const canAttendNow = switchChecked && !saving;

  const loadHealthcareService = async (
    schedule: fhir5.Schedule,
    mapped: ProntoAtendimentoConfig | null,
    healthcareServicesById?: Map<string, fhir5.HealthcareService>,
  ) => {
    const hcsId =
      mapped?.healthcareServiceId ||
      getHealthcareServiceIdFromSchedule(schedule);
    if (!hcsId) {
      setHealthcareService(null);
      return;
    }
    const fromBundle = healthcareServicesById?.get(hcsId);
    const hcs = fromBundle ?? (await fetchHealthcareServiceById(hcsId));
    setHealthcareService(hcs ?? null);
  };

  const loadConfig = useCallback(async () => {
    if (!organizationToken || !user.username) return;
    setLoading(true);
    try {
      const bundle = await GetSchedule({
        practitioner: practitionerRef,
        organization: organizationToken,
        includeLocation: true,
        includeHealthcareService: true,
        serviceCategory: PRONTO_ATENDIMENTO_SERVICE_CATEGORY_TOKEN,
      });

      const { schedules, locationsById } = extractSchedulesAndLocations(bundle);
      const healthcareServicesById = extractHealthcareServicesFromBundle(bundle);
      const prontoSchedules = schedules.filter(isProntoAtendimentoSchedule);
      const chosen = pickSingleSchedule(prontoSchedules);

      if (!chosen?.id) {
        setConfig((prev) => (prev && !prev.active ? prev : null));
        setScheduleRaw((prev) =>
          prev && prev.active === false ? prev : null,
        );
        setHealthcareService(null);
        return;
      }

      setScheduleRaw(chosen);
      const mapped = mapScheduleToConfig(chosen, locationsById);
      setConfig(mapped);
      await loadHealthcareService(chosen, mapped, healthcareServicesById);
    } catch (error) {
      console.error("useProntoAtendimentoConfigs.loadConfig", error);
      toast.error(
        "Não foi possível carregar a agenda de Pronto Atendimento.",
      );
      setConfig(null);
      setScheduleRaw(null);
      setHealthcareService(null);
    } finally {
      setLoading(false);
    }
  }, [organizationToken, practitionerRef, user.username]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /** Cria/edita agenda + (re)configura o único HCS (especialidade + horários de hoje). */
  const upsertConfig = async (input: ProntoAtendimentoConfigInput) => {
    if (!access?.organizationId) {
      toast.error("Organização não encontrada.");
      return;
    }
    if (!input.specialty?.trim()) {
      toast.info("Selecione a especialidade.");
      return;
    }
    if (!input.todayIntervals?.length) {
      toast.info("Informe o horário de disponibilidade de hoje.");
      return;
    }

    setSaving(true);
    try {
      const loc = await ensureProntoAtendimentoLocation({
        mode: input.mode,
        organizationId: access.organizationId,
        locationEntries: location,
        selectedLocationId: input.selectedLocationId,
        newLocation: input.newLocation,
        refetchLocation,
      });

      // 1 HCS: cria ou atualiza o já vinculado ao Schedule
      const existingHcsId =
        config?.healthcareServiceId ||
        scheduleRaw?.actor
          ?.find((a) => a.reference?.includes("HealthcareService/"))
          ?.reference?.split("/")?.[1];

      const hcsPayload = buildProntoHealthcareServicePayload({
        specialty: input.specialty.trim(),
        intervals: input.todayIntervals,
      });

      const hcs = await putProntoAtendimentoHealthcareService(
        hcsPayload,
        access.organizationId,
        existingHcsId,
      );
      clearProntoHealthcareServiceCache();

      const name = `${PRONTO_ATENDIMENTO_MODE_LABELS[input.mode]} — R$ ${input.value
        .toFixed(2)
        .replace(".", ",")}`;

      const hcsActorValue = {
        reference: `HealthcareService/${hcs.id}`,
        display: hcs.name || input.specialty.trim(),
      };

      if (!config?.id || !scheduleRaw) {
        await postProntoAtendimentoSchedule({
          name,
          mode: input.mode,
          value: input.value,
          isDefault: true,
          locationId: loc.id,
          locationDisplay: loc.name,
          practitionerRef,
          practitionerDisplay,
          healthcareServiceId: hcs.id,
          healthcareServiceDisplay: hcsActorValue.display,
        });
        toast.success("Pronto Atendimento configurado.");
      } else {
        const schedule = scheduleRaw;
        const ops: Array<{ op: string; path: string; value?: unknown }> = [];

        if (name !== schedule.name) {
          ops.push({ op: "replace", path: "/name", value: name });
        }

        const costIdx = findExtensionIndex(schedule, SERVICE_COST_EXTENSION_URL);
        if (costIdx >= 0) {
          ops.push({
            op: "replace",
            path: `/extension/${costIdx}/valueDecimal`,
            value: input.value,
          });
        } else {
          ops.push({
            op: "add",
            path: schedule.extension ? "/extension/-" : "/extension",
            value: schedule.extension
              ? { url: SERVICE_COST_EXTENSION_URL, valueDecimal: input.value }
              : [{ url: SERVICE_COST_EXTENSION_URL, valueDecimal: input.value }],
          });
        }

        const attendanceType = modeToAttendanceType(input.mode);
        const attIdx = findExtensionIndex(
          schedule,
          SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
        );
        if (attIdx >= 0) {
          ops.push({
            op: "replace",
            path: `/extension/${attIdx}/valueCode`,
            value: attendanceType,
          });
        } else {
          ops.push({
            op: "add",
            path: schedule.extension ? "/extension/-" : "/extension",
            value: schedule.extension
              ? {
                  url: SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
                  valueCode: attendanceType,
                }
              : [
                  {
                    url: SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
                    valueCode: attendanceType,
                  },
                ],
          });
        }

        const locActorIdx =
          schedule.actor?.findIndex((a) =>
            a.reference?.includes("Location/"),
          ) ?? -1;
        const locActorValue = {
          reference: `Location/${loc.id}`,
          display: loc.name,
        };
        if (locActorIdx >= 0) {
          ops.push({
            op: "replace",
            path: `/actor/${locActorIdx}`,
            value: locActorValue,
          });
        }

        // Garante 1 actor HealthcareService no Schedule
        const hcsActorIdx =
          schedule.actor?.findIndex((a) =>
            a.reference?.includes("HealthcareService/"),
          ) ?? -1;
        if (hcsActorIdx >= 0) {
          ops.push({
            op: "replace",
            path: `/actor/${hcsActorIdx}`,
            value: hcsActorValue,
          });
        } else {
          ops.push({
            op: "add",
            path: "/actor/-",
            value: hcsActorValue,
          });
        }

        if (input.activate === true && schedule.active !== true) {
          ops.push({
            op: schedule.active === undefined ? "add" : "replace",
            path: "/active",
            value: true,
          });
        }

        if (ops.length > 0) {
          await PatchSchedule(config.id, ops);
        }
        toast.success("Pronto Atendimento atualizado.");
      }

      await loadConfig();
    } catch (error) {
      console.error("upsertConfig", error);
      toast.error("Não foi possível salvar a configuração.");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const setAvailable = async (available: boolean) => {
    if (!config?.id) {
      if (available) {
        toast.info("Configure o Pronto Atendimento antes de ativar.");
      }
      return;
    }
    if (available) return;

    setSaving(true);
    try {
      await PatchSchedule(config.id, [
        { op: "replace", path: "/active", value: false },
      ]);
      setConfig((prev) => (prev ? { ...prev, active: false } : null));
      setScheduleRaw((prev) => (prev ? { ...prev, active: false } : null));
      toast.success("Pronto Atendimento desativado.");
      await loadConfig();
    } catch (error) {
      console.error("setAvailable", error);
      toast.error("Não foi possível desativar o Pronto Atendimento.");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return {
    config,
    isAvailable,
    switchChecked,
    canAttendNow,
    isWithinServiceHours,
    isExpiredOutsideHours,
    hasTimeWindow,
    loading,
    saving,
    presencialLocations,
    refetch: loadConfig,
    upsertConfig,
    setAvailable,
  };
}
